/**
 * RPC method names — dot-notation strings matching the platform's handleCharxRpc switch cases.
 * Used as the `method` field in { type: '__charx_rpc__', method, args[] } messages.
 */
export const METHODS = {
  // ── Character ──────────────────────────────────────────────────────────────
  CHARACTER_GET_INFO:      'character.getInfo',
  CHARACTER_GET_PERSONA:   'character.getPersona',
  CHARACTER_GET_SETTINGS:  'character.getSettings',

  // ── Chat ───────────────────────────────────────────────────────────────────
  CHAT_SEND:                    'chat.send',
  CHAT_STOP:                    'chat.stop',
  CHAT_REGENERATE:              'chat.regenerate',
  CHAT_CONTINUE:                'chat.continue',
  CHAT_SET_INPUT:               'chat.setInput',
  CHAT_GET_INPUT:               'chat.getInput',
  CHAT_SET_GREETING_INDEX:      'chat.setGreetingIndex',
  CHAT_GET_GREETING_INDEX:      'chat.getGreetingIndex',
  CHAT_SET_FIRST_MES:           'chat.setFirstMesIndex',
  /** Fetch conversation history. Platform args: [{ limit?, offset? }] */
  CHAT_GET_HISTORY:             'chat.getHistory',
  /** @deprecated Platform doesn't handle this; use CHAT_GET_HISTORY */
  CHAT_MESSAGE_LIST:            'chat.messages.list',
  CHAT_MESSAGE_DELETE:          'chat.messages.delete',
  CHAT_MESSAGE_EDIT:            'chat.messages.edit',
  /** @deprecated Compatibility placeholder only; current hosts may reject this call. */
  CHAT_MESSAGE_SWIPE:           'chat.messages.swipe',
  /** Silent background LLM generation — equivalent to SillyTavern generateRaw() */
  CHAT_GENERATE_RAW:            'chat.generateRaw',
  /** Inject a system prompt into the next user-facing generation call */
  CHAT_INJECT_PROMPT:           'chat.injectPrompt',
  /** Insert a system message into the conversation without triggering AI generation */
  CHAT_INSERT_SYSTEM_MESSAGE:   'chat.insertSystemMessage',
  /** Get the current conversation ID */
  CHAT_GET_CONVERSATION_ID:     'chat.getConversationId',

  // ── Variable ───────────────────────────────────────────────────────────────
  VARIABLE_GET:            'variable.get',
  VARIABLE_GET_ALL:        'variable.getAll',
  VARIABLE_SET:            'variable.set',
  VARIABLE_SET_MANY:       'variable.setMany',
  VARIABLE_INCREMENT:      'variable.increment',
  VARIABLE_DECREMENT:      'variable.decrement',
  VARIABLE_DELETE:         'variable.delete',
  VARIABLE_CLEAR:          'variable.clear',

  // ── Storage ────────────────────────────────────────────────────────────────
  STORAGE_GET:             'storage.get',
  STORAGE_SET:             'storage.set',
  STORAGE_REMOVE:          'storage.remove',
  STORAGE_CLEAR:           'storage.clear',
  STORAGE_KEYS:            'storage.keys',

  // ── UI ─────────────────────────────────────────────────────────────────────
  UI_TOAST:                'ui.toast',
  UI_ALERT:                'ui.alert',
  UI_CONFIRM:              'ui.confirm',
  UI_PROMPT:               'ui.prompt',
  UI_PANEL_OPEN:           'ui.openPanel',
  UI_PANEL_CLOSE:          'ui.closePanel',
  UI_CHAT_INPUT_HIDE:      'ui.hideChatInput',
  UI_CHAT_INPUT_SHOW:      'ui.showChatInput',
  UI_THEME_SET:            'ui.setTheme',
  UI_SCROLL_TO_BOTTOM:     'ui.scrollToBottom',

  // ── Media ──────────────────────────────────────────────────────────────────
  MEDIA_SPEAK:             'media.speak',
  MEDIA_SPEAK_STOP:        'media.stopSpeak',
  MEDIA_BGM_PLAY:          'media.bgm.play',
  MEDIA_BGM_STOP:          'media.bgm.stop',
  MEDIA_BGM_PAUSE:         'media.bgm.pause',
  MEDIA_BGM_RESUME:        'media.bgm.resume',
  MEDIA_BGM_VOLUME:        'media.bgm.setVolume',
  MEDIA_SFX_PLAY:          'media.sfx.play',
  MEDIA_SFX_PRELOAD:       'media.sfx.preload',

  // ── Worldbook ──────────────────────────────────────────────────────────────
  WORLDBOOK_GET_ALL:         'worldbook.getAll',
  WORLDBOOK_TOGGLE:          'worldbook.toggle',
  WORLDBOOK_BATCH_TOGGLE:    'worldbook.batchToggle',
  WORLDBOOK_SET_CONTENT:     'worldbook.setContent',
  WORLDBOOK_CLEAR_CONTENT:   'worldbook.clearContent',
  WORLDBOOK_RESET:           'worldbook.reset',

  // ── Game ───────────────────────────────────────────────────────────────────
  GAME_SAVE:               'game.save',
  GAME_LOAD:               'game.load',
  GAME_SAVE_LIST:          'game.listSaves',
  GAME_SAVE_DELETE:        'game.deleteSave',
  GAME_ACHIEVEMENT_UNLOCK: 'game.achievements.unlock',
  GAME_ACHIEVEMENT_LIST:   'game.achievements.list',
  GAME_RANKING_SUBMIT:     'game.ranking.submit',
  GAME_RANKING_TOP:        'game.ranking.getTop',
  GAME_RANKING_SELF:       'game.ranking.getSelf',
} as const

/**
 * Push event names — platform sends `{ type: '__charx_push__', event, data }`
 * to iframes to notify them of state changes.
 */
export const PUSH = {
  /**
   * Platform pushes this after a generation request is accepted.
   * Payload: `{ requestId, userMessageId?, assistantMessageId? }`
   */
  CHAT_STARTED:            'chat.started',
  VARIABLE_CHANGED:        'variable.changed',
  /** Platform pushes this when a new chat message is received */
  CHAT_MESSAGE:            'chat.message',
  /**
   * Platform pushes streaming chunks.
   * Payload: `{ chunk: string; done: boolean; messageId: string | number }`
   * When `done === true`, the stream is complete.
   */
  CHAT_STREAM_CHUNK:       'chat.stream_chunk',
  /**
   * Platform pushes this when a chat generation fails mid-stream.
   * Payload: `{ error: string }`
   */
  CHAT_ERROR:              'chat.error',
  /**
   * Platform pushes this when the active conversation changes (user switches chat).
   * Payload: `{ conversationId: number }`
   * Equivalent to SillyTavern CHAT_CHANGED event.
   */
  CHAT_CHANGED:            'chat.changed',
  /**
   * Platform pushes this after a message is saved/updated (stream complete or manual edit via SDK).
   * Payload: `{ id: string; role: string; content: string; createdAt: number }`
   * Equivalent to SillyTavern MESSAGE_UPDATED event.
   */
  MESSAGE_UPDATED:         'message.updated',
  /**
   * Platform pushes this when a user manually edits a message via sdk.chat.messages.edit().
   * Payload: `{ id: string; role: string; content: string; createdAt: number }`
   * Equivalent to SillyTavern MESSAGE_EDITED event.
   */
  MESSAGE_EDITED:          'message.edited',
  /**
   * Platform pushes this when a message is deleted via sdk.chat.messages.delete().
   * Payload: `{ id: string; role: string; content: string; createdAt: number }`
   * Equivalent to SillyTavern MESSAGE_DELETED event.
   */
  MESSAGE_DELETED:         'message.deleted',
} as const

/** @deprecated Use METHODS instead */
export { METHODS as EVENT }
